import type { Components } from '@/api/generated/api';
import type { ISelectedCompetence } from '@/interfaces';
import { getOrderedSchoolSubjects } from 'common/utils/school.subject.utils';
import { cloneArray, immutableSort, toRecord } from 'common/utils/array.utils';

export type TCompetenceDomainsWithSchoolSubject = {
  schoolSubject: Components.Schemas.SchoolSubjectDto | null;
  competenceDomains: Components.Schemas.CompetenceDomainDto[];
};

export const toCompetenceSelectionRecord = (
  domains: Components.Schemas.CompetenceDomainDto[],
): Partial<Record<number, ISelectedCompetence>> =>
  toRecord(
    domains.flatMap((domain) => domain.competences.map((competence) => ({ competence, domain }))),
    (selection) => selection.competence.id,
    (selection) => selection,
  );

const compareByHelper =
  <T>(get: (x: T) => string | null | undefined) =>
  (a: T, b: T) =>
    (get(a) ?? '').localeCompare(get(b) ?? '');

type TCompetenceOrder = Pick<Components.Schemas.CompetenceDto, 'id' | 'description' | 'order'>;

/**
 * Sort strategy:
 * sorts competenceDomains alphabetically by name
 * sorts competences by order once an order was set, otherwise keeps the previous alphabetical order
 * returns a new array -> doesn't mutate the input!
 */
export function sortCompetenceDomains<TDomain extends { name: string; competences: TCompetenceOrder[] }>(
  domains: TDomain[] | undefined | null,
): TDomain[] {
  const byDomainName = compareByHelper<TDomain>((d) => d.name);
  const byCompetenceDescription = compareByHelper<TCompetenceOrder>((c) => c.description);
  const byCompetenceOrder = (a: TCompetenceOrder, b: TCompetenceOrder): number =>
    a.order - b.order || byCompetenceDescription(a, b) || a.id - b.id;

  // checks if at least one competence has an order set, if not we keep the previous alphabetical order
  const getCompetenceSorter = (
    competences: TCompetenceOrder[],
  ): ((a: TCompetenceOrder, b: TCompetenceOrder) => number) => {
    return competences.some((competence) => competence.order !== 0) ? byCompetenceOrder : byCompetenceDescription;
  };

  const withSortedCompetences = cloneArray(domains).map((domain) => ({
    ...domain,
    competences: immutableSort(cloneArray(domain.competences), getCompetenceSorter(domain.competences)),
  }));

  return immutableSort(withSortedCompetences, byDomainName);
}

/** Groups domains by configured subject/group order and sorts domains alphabetically within each subject. */
export const groupCompetenceDomainsBySchoolSubject = (
  domains: Components.Schemas.CompetenceDomainDto[],
  schoolSubjects: Components.Schemas.SchoolSubjectDto[],
  schoolSubjectGroups: Components.Schemas.SchoolSubjectGroupDto[],
): TCompetenceDomainsWithSchoolSubject[] => {
  const orderedSchoolSubjects = getOrderedSchoolSubjects(schoolSubjects, schoolSubjectGroups);
  const orderedSubjectIds = new Set(orderedSchoolSubjects.map((subject) => subject.id));
  const subjectsInDisplayOrder = [
    ...orderedSchoolSubjects,
    ...schoolSubjects.filter((subject) => !orderedSubjectIds.has(subject.id)),
  ];
  const schoolSubjectById = new Map(schoolSubjects.map((subject) => [subject.id, subject]));
  const domainsBySubjectId = new Map<number, Components.Schemas.CompetenceDomainDto[]>();
  const subjectlessDomains: Components.Schemas.CompetenceDomainDto[] = [];
  const sortDomains = (values: Components.Schemas.CompetenceDomainDto[]): Components.Schemas.CompetenceDomainDto[] =>
    immutableSort(values, (a, b) => a.name.localeCompare(b.name) || a.id - b.id);

  for (const domain of domains) {
    if (domain.schoolSubjectId == null || !schoolSubjectById.has(domain.schoolSubjectId)) {
      subjectlessDomains.push(domain);
      continue;
    }
    const subjectDomains = domainsBySubjectId.get(domain.schoolSubjectId) ?? [];
    subjectDomains.push(domain);
    domainsBySubjectId.set(domain.schoolSubjectId, subjectDomains);
  }

  const groupedDomains = subjectsInDisplayOrder.flatMap<TCompetenceDomainsWithSchoolSubject>((schoolSubject) => {
    const subjectDomains = domainsBySubjectId.get(schoolSubject.id);
    return subjectDomains?.length ? [{ schoolSubject, competenceDomains: sortDomains(subjectDomains) }] : [];
  });

  if (subjectlessDomains.length) {
    groupedDomains.push({ schoolSubject: null, competenceDomains: sortDomains(subjectlessDomains) });
  }

  return groupedDomains;
};

export const getAccordionIdentifier = (domainId: number): string => {
  return `competence-domain-${domainId}`;
};
